import React from 'react';
import { getBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';
import TooltipWrapper from 'components/shared/TooltipWrapper';

/**
 * ReferralStatsBlock - Shared component for displaying referral statistics
 *
 * Used in both edit mode (preview with sample data) and public mode (real count).
 * Shows the number of students who booked through teacher referrals.
 *
 * @param {Object} props
 * @param {number} props.count - Referral bookings count
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (with sample) or 'public' (real data only)
 * @param {boolean} props.showSample - Show sample data when count is 0 (edit mode)
 * @param {Object} props.pageDesign - Page design configuration for text styling
 */
function ReferralStatsBlock({
  count,
  translations,
  mode = 'public',
  showSample = false,
  pageDesign
}) {
  // Determine if we should show sample data
  const hasRealData = count && count > 0;
  const shouldShowSample = mode === 'edit' && showSample && !hasRealData;

  // Use real count or sample count
  const displayCount = hasRealData ? count : (shouldShowSample ? 5 : 0);

  // Don't render if no data and not showing sample
  if (!displayCount && !shouldShowSample) return null;

  // Get label translation
  const label = translations?.referred_bookings_count || 'Students Taught via Referrals';

  // Use different tooltip text based on mode (edit vs public)
  const tooltipKey = mode === 'public' ? 'referred_bookings_count_tooltip_public' : 'referred_bookings_count_tooltip';
  const defaultTooltip = mode === 'public'
    ? 'These students came via referrals from other users, indicating the teacher has a strong teaching reputation.'
    : 'These students came via referrals from other users, indicating you have a strong teaching reputation.';
  const tooltipText = translations?.[tooltipKey] || defaultTooltip;

  // Get text styles - title for label (heading), content for number (value)
  const blockStyle = getBlockStyle(pageDesign, 'referral_stats');
  const titleTextStyle = getTextStyle(blockStyle, 'title');      // For label (heading text)
  const contentTextStyle = getTextStyle(blockStyle, 'content');  // For number (actual value)

  return (
    <div className="referral-stats-block">
      {/* Tooltip container */}
      <TooltipWrapper tooltip={tooltipText}>
        <div className="rounded-2xl">
          <div className="flex flex-col items-center justify-center gap-2">
            {/* Label on top - uses titleTextStyle */}
            <div
              className={`flex items-center justify-center gap-1 ${generateTextStyleClasses(titleTextStyle, pageDesign)}`}
              style={generateTextStyleInlineStyles(titleTextStyle)}
            >
              <span>{label}</span>
              <span className="opacity-60">ℹ️</span>
            </div>

            {/* Number and icon on bottom - number uses contentTextStyle */}
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl">🎓</span>
              <div
                className={`font-bold ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
                style={generateTextStyleInlineStyles(contentTextStyle)}
              >
                {displayCount}
              </div>
            </div>
          </div>
        </div>
      </TooltipWrapper>

      {/* Sample hint (edit mode only) */}
      {shouldShowSample && (
        <p className="text-xs text-gray-500 mt-3 italic text-center">
          💡 {translations?.inline_editor?.sample_data?.referral_stats?.description || 'This is sample data. Students referred to you will appear here.'}
        </p>
      )}
    </div>
  );
}

export default ReferralStatsBlock;
